import {type RouteLocationStringParam, type RouteRecordRaw} from 'vue-router';
import {toInt} from '../../library/math';
import {ROUTE_REF_PARAM_DEFINITION_SLASH} from '../../utils/portuna';

const SessionserviceIndex        = () => import('../../views/sessionservice/SessionserviceIndex.vue');
const SessionserviceList         = () => import('../../views/sessionservice/SessionserviceList.vue');
const SessionserviceListDocument = () => import('../../views/sessionservice/list/SessionserviceListDocument.vue');
const SessionserviceListSession  = () => import('../../views/sessionservice/list/SessionserviceListSession.vue');
const SessionserviceEntryEdit    = () => import('../../views/sessionservice/entry/SessionserviceEntryEdit.vue');
const SessionserviceEntryView    = () => import('../../views/sessionservice/entry/SessionserviceEntryView.vue');

const SessionserviceEntryMail     = () => import('../../views/sessionservice/entry/SessionserviceEntryMail.vue');
const SessionserviceEntryMailList = () => import('../../views/sessionservice/entry/SessionserviceEntryMailList.vue');
const SessionserviceEntryMailView = () => import('../../views/sessionservice/entry/SessionserviceEntryMailView.vue');

const SessionservicePersonEdit   = () => import('../../views/sessionservice/person/SessionservicePersonEdit.vue');
const SessionservicePersonView   = () => import('../../views/sessionservice/person/SessionservicePersonView.vue');
const SessionservicePersonList   = () => import('../../views/sessionservice/person/SessionservicePersonList.vue');
const SessionservicePersonExport = () => import('../../views/sessionservice/person/SessionservicePersonExport.vue');

const SessionserviceGroupEdit = () => import('../../views/sessionservice/group/SessionserviceGroupEdit.vue');
const SessionserviceGroupView = () => import('../../views/sessionservice/group/SessionserviceGroupView.vue');
const SessionserviceGroupList = () => import('../../views/sessionservice/group/SessionserviceGroupList.vue');
const SessionserviceGroupSort = () => import('../../views/sessionservice/group/SessionserviceGroupSort.vue');

const SessionserviceTemplateList = () => import('../../views/sessionservice/template/SessionserviceTemplateList.vue');
const SessionserviceSettings     = () => import('../../views/sessionservice/settings/SessionserviceSettings.vue');

const SessionserviceSearch = () => import('../../views/sessionservice/SessionserviceSearch.vue');

export function addSessionserviceRoutes(routes: RouteRecordRaw[], resource: SessionserviceModule, meta: MetaData)
{

    //<editor-fold desc="List routes">
    routes.push(
        {
            name: resource + '-index',
            path: '/' + resource,
            meta,
            component: SessionserviceIndex,
            props()
            {
                return {
                    resource,
                };
            },
        },
        // old compatibility route
        {
            path: '/' + resource + '/session/documents/:wildcard*',
            redirect: {name: resource + '-list-document'},
            meta,
        },
        {
            name: resource + '-list',
            path: '/' + resource + '/session/list',
            meta,
            component: SessionserviceList,
            props()
            {
                return {
                    resource,
                };
            },
            children: [
                {
                    name: resource + '-list-document',
                    path: 'document/:mine(mine)?',
                    meta,
                    component: SessionserviceListDocument,
                    props(route: RouteLocationStringParam)
                    {
                        return {
                            mine: !!route.params.mine,
                            resource,
                        };
                    },
                },
                {
                    name: resource + '-list-protocol',
                    path: 'protocol/:mine(mine)?',
                    meta,
                    component: SessionserviceListDocument,
                    props(route: RouteLocationStringParam)
                    {
                        return {
                            mine: !!route.params.mine,
                            protocol: true,
                            resource,
                        };
                    },
                },
                {
                    name: resource + '-list-group',
                    path: 'group/:groupId',
                    meta,
                    component: SessionserviceListSession,
                    props(route: RouteLocationStringParam)
                    {
                        return {
                            group: parseInt(route.params.groupId),
                            resource,
                        };
                    },
                },
                {
                    name: resource + '-list-session',
                    path: ':mine(mine)?',
                    meta,
                    component: SessionserviceListSession,
                    props(route: RouteLocationStringParam)
                    {
                        return {
                            mine: !!route.params.mine,
                            resource,
                        };
                    },
                },
                // fallback
                {
                    path: ':wildcard*',
                    redirect: {name: resource + '-list-session'},
                },
            ],
        },
    );

    routes.push({
        path: '/' + resource + '/session/mine/:wildcard*',
        redirect: {
            name: resource + '-list-session',
            params: {
                mine: 'mine',
                hash: '',
            },
        },
    });
    routes.push({
        path: '/' + resource + '/session/documents/:wildcard*',
        redirect: {name: resource + '-list-document'},
    });
    //</editor-fold>

    //<editor-fold desc="entry">
    // entry add routes
    routes.push(
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit/parent/:calendar/:wildcard*',
            redirect: {name: resource + '-entry-add'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit/calendar/:calendar/:wildcard*',
            redirect: {name: resource + '-entry-add'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit',
            redirect: {name: resource + '-entry-add'},
            meta,
        },
        {
            name: resource + '-entry-add',
            component: SessionserviceEntryEdit,
            path: '/' + resource + '/entry/add' + ROUTE_REF_PARAM_DEFINITION_SLASH,
            meta,
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    template: toInt(route.query.template),
                };
            },
        },
    );

    // entry edit routes
    routes.push(
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit/id/:id/start/:start',
            redirect: {name: resource + '-entry-edit'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit/id/:id/start/:start/:wildcard*',
            redirect: {name: resource + '-entry-edit'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit/id/:id/:wildcard*',
            redirect: {name: resource + '-entry-edit'},
            meta,
        },
        {
            name: resource + '-entry-edit',
            component: SessionserviceEntryEdit,
            path: '/' + resource + '/entry/edit/:id/:start?' + ROUTE_REF_PARAM_DEFINITION_SLASH,
            alias: ['/' + resource + '/entry/edit/:id/:start?/:wildcard*'],
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                    start: route.params.start || '',
                    template: toInt(route.query.template),
                };
            },
            meta,
        },
        {
            name: resource + '-entry-copy',
            component: SessionserviceEntryEdit,
            path: '/' + resource + '/entry/copy/:id/:start?' + ROUTE_REF_PARAM_DEFINITION_SLASH,
            alias: ['/' + resource + '/entry/copy/:id/:start?/:wildcard*'],
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                    start: route.params.start || '',
                };
            },
            meta,
        },
    );

    // entry view routes
    routes.push(
        // old compatibility route
        {
            path: '/' + resource + '/entry/view/id/:id/start/:start',
            redirect: {name: resource + '-entry-view'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/view/id/:id/start/:start/:wildcard*',
            redirect: {name: resource + '-entry-view'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/view/id/:id/:wildcard*',
            redirect: {name: resource + '-entry-view'},
            meta,
        },
        {
            name: resource + '-entry-view',
            component: SessionserviceEntryView,
            path: '/' + resource + '/entry/view/:id/:start?' + ROUTE_REF_PARAM_DEFINITION_SLASH,
            alias: ['/' + resource + '/entry/view/:id/:start?/:wildcard*'],
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                    start: route.params.start || '',
                };
            },
            meta,
        },
    );

    // entry mail routes
    routes.push(
        // old compatibility route - mail edit
        {
            path: '/' + resource + '/entry/mail/id/:id/start/:start',
            redirect: {name: resource + '-entry-mail'},
            meta,
        },
        // old compatibility route - mail edit
        {
            path: '/' + resource + '/entry/mail/id/:id/start/:start/:wildcard*',
            redirect: {name: resource + '-entry-mail'},
            meta,
        },
        // old compatibility route - mail view
        {
            path: '/' + resource + '/entry/view-mail/id/:id/:wildcard*',
            redirect: {name: resource + '-entry-mail-view'},
            meta,
        },
        // old compatibility route - mail list
        {
            path: '/' + resource + '/entry/mail-list/id/:id/start/:start/:wildcard*',
            redirect: {name: resource + '-entry-mail-list'},
            meta,
        },
        {
            name: resource + '-entry-mail',
            component: SessionserviceEntryMail,
            path: '/' + resource + '/entry/mail/:id/:start?',
            alias: ['/' + resource + '/entry/mail/:id/:start?/:wildcard*'],
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                    start: route.params.start || '',
                };
            },
            meta,
        },
        {
            name: resource + '-entry-mail-view',
            component: SessionserviceEntryMailView,
            path: '/' + resource + '/entry/mail-view/:id',
            alias: ['/' + resource + '/entry/mail-view/:id/:wildcard*'],
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                };
            },
            meta,
        },
        {
            name: resource + '-entry-mail-list',
            component: SessionserviceEntryMailList,
            path: '/' + resource + '/entry/mail-list/:id/:start?',
            alias: ['/' + resource + '/entry/mail-list/:id/:start?/:wildcard*'],
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                    start: route.params.start || '',
                };
            },
            meta,
        },
    );
    //</editor-fold>

    //<editor-fold desc="template routes">
    routes.push(
        {
            name: resource + '-template-list',
            path: '/' + resource + '/template/list',
            alias: '/' + resource + '/template/templates-list',
            meta,
            component: SessionserviceTemplateList,
            props()
            {
                return {
                    resource,
                };
            },
        },
        {
            name: resource + '-template-add',
            component: SessionserviceEntryEdit,
            path: '/' + resource + '/template/add',
            meta,
            props()
            {
                return {
                    resource,
                };
            },
        },
        {
            name: resource + '-template-edit',
            component: SessionserviceEntryEdit,
            path: '/' + resource + '/template/edit/:id(\\d+)?',
            meta,
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                };
            },
        },
        {
            name: resource + '-template-copy',
            component: SessionserviceEntryEdit,
            path: '/' + resource + '/template/copy/:id(\\d+)?',
            meta,
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                };
            },
        },
        {
            name: resource + '-template-view',
            component: SessionserviceEntryView,
            path: '/' + resource + '/template/view/:id',
            alias: '/' + resource + '/template/view/:id/:wildcard*',
            meta,
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                };
            },
        },
    );
    //</editor-fold>

    //<editor-fold desc="person routes">
    routes.push(
        {
            name: resource + '-person-list',
            path: '/' + resource + '/person/list',
            meta,
            component: SessionservicePersonList,
            props()
            {
                return {
                    resource,
                };
            },
        },
        {
            name: resource + '-person-add',
            component: SessionservicePersonEdit,
            path: '/' + resource + '/person/add',
            meta,
            props()
            {
                return {
                    resource,
                };
            },
        },
        {
            name: resource + '-person-edit',
            component: SessionservicePersonEdit,
            path: '/' + resource + '/person/edit/:id(\\d+)?',
            meta,
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                };
            },
        },
        // old compatibility route
        {
            path: '/' + resource + '/person/view/id/:id',
            alias: '/' + resource + '/person/view/id/:id/:wildcard*',
            redirect: {name: resource + '-person-view'},
            meta,
        },
        {
            name: resource + '-person-view',
            component: SessionservicePersonView,
            path: '/' + resource + '/person/view/:id',
            alias: '/' + resource + '/person/view/:id/:wildcard*',
            meta,
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                };
            },
        },
        {
            name: resource + '-person-export',
            component: SessionservicePersonExport,
            path: '/' + resource + '/person/export',
            meta,
            props()
            {
                return {
                    resource,
                };
            },
        },
    );
    //</editor-fold>

    //<editor-fold desc="group routes">
    routes.push(
        {
            name: resource + '-group-list',
            path: '/' + resource + '/group/list',
            meta,
            component: SessionserviceGroupList,
            props()
            {
                return {
                    resource,
                };
            },
        },
        {
            name: resource + '-group-sort',
            path: '/' + resource + '/group/sort',
            meta,
            component: SessionserviceGroupSort,
            props()
            {
                return {
                    resource,
                };
            },
        },
        {
            name: resource + '-group-add',
            component: SessionserviceGroupEdit,
            path: '/' + resource + '/group/add',
            meta,
            props()
            {
                return {
                    resource,
                };
            },
        },
        // old compatibility route
        {
            path: '/' + resource + '/group/edit/id/:id',
            alias: '/' + resource + '/group/edit/id/:id/:wildcard*',
            redirect: {name: resource + '-group-edit'},
            meta,
        },
        {
            name: resource + '-group-edit',
            component: SessionserviceGroupEdit,
            path: '/' + resource + '/group/edit/:id(\\d+)?',
            meta,
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                };
            },
        },
        {
            path: '/' + resource + '/group/view/id/:id',
            alias: '/' + resource + '/group/view/id/:id/:wildcard*',
            redirect: {name: resource + '-group-view'},
            meta,
        },
        {
            name: resource + '-group-view',
            component: SessionserviceGroupView,
            path: '/' + resource + '/group/view/:id',
            alias: '/' + resource + '/group/view/:id/:wildcard*',
            meta,
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                };
            },
        },
        {
            path: '/' + resource + '/group/:wildcard*',
            redirect: {name: resource + '-group-list'},
        },
    );
    //</editor-fold>

    //<editor-fold desc="setting routes">
    const settingsMeta: MetaData = Object.assign({noRouteResetIfSame: resource + '-settings'}, meta);
    routes.push(
        {
            name: resource + '-settings',
            path: '/' + resource + '/settings/:route(intranet)?',
            meta,
            component: SessionserviceSettings,
            props()
            {
                return {
                    resource,
                };
            },
            children: [
                {
                    name: resource + '-settings-index',
                    path: 'index',
                    meta: settingsMeta,
                    component: SessionserviceSettings,
                },
                {
                    name: resource + '-settings-representative',
                    path: 'representative',
                    meta: settingsMeta,
                    component: SessionserviceSettings,
                },
                {
                    name: resource + '-settings-person',
                    path: 'person',
                    meta: settingsMeta,
                    component: SessionserviceSettings,
                },
                {
                    name: resource + '-settings-document',
                    path: 'document',
                    meta: settingsMeta,
                    component: SessionserviceSettings,
                },
                {
                    name: resource + '-settings-session',
                    path: 'session',
                    meta: settingsMeta,
                    component: SessionserviceSettings,
                },
                {
                    name: resource + '-settings-export',
                    path: 'export',
                    meta: settingsMeta,
                    component: SessionserviceSettings,
                },
                {
                    name: resource + '-settings-member',
                    path: 'member',
                    meta: settingsMeta,
                    component: SessionserviceSettings,
                },
                {
                    name: resource + '-settings-groups-view',
                    path: 'groups-view',
                    meta: settingsMeta,
                    component: SessionserviceSettings,
                },
                // fallback
                {

                    path: ':wildcard*',
                    redirect: {name: resource + '-settings-index'},
                },
            ],
        },
    );
    //</editor-fold>

    //<editor-fold desc="search routes">
    routes.push({
        name: resource + '-searched',
        path: '/' + resource + '/search/q/:input/:wildcard*',
        meta,
        component: SessionserviceSearch,
        props(route: RouteLocationStringParam)
        {
            return {
                resource,
                input: route.params.input,
            };
        },
    });

    routes.push({
        name: resource + '-search',
        path: '/' + resource + '/search/:wildcard*',
        meta,
        component: SessionserviceSearch,
        props()
        {
            return {
                resource,
            };
        },
    });
    //</editor-fold>

    routes.push({
        path: '/' + resource + '/:wildcard*',
        redirect: {name: resource + '-index'},
    });
}